import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";

import { camelizeKeys } from "humps";

import { EligibilityPermit } from "~/components/pages/complianceManagement/personnel/eligibility-permits/types";
import { EligibilityPermitProps } from "~/components/pages/complianceManagement/personnel/eligibility-permits/create/types";

import { CompetencePermit } from "~/components/pages/complianceManagement/personnel/competence-permits/types";
import { CompetencePermitProps } from "~/components/pages/complianceManagement/personnel/competence-permits/create/types";

import { CompanyWidePermit } from "~/components/pages/complianceManagement/organizational/company-wide-permits/types";
import { CompanyWidePermitProps } from "~/components/pages/complianceManagement/organizational/company-wide-permits/create/types";

import { SiteSpecificPermit } from "~/components/pages/complianceManagement/organizational/site-specific-permits/types";
import { SiteSpecificPermitProps } from "~/components/pages/complianceManagement/organizational/site-specific-permits/create/types";

import { DashboardProps } from "~/components/pages/dashboard/types";
import { Audit } from "~/components/pages/itemManagement/audits/types";

import { Finding } from "~/components/pages/itemManagement/incidents/create/steps/findings/types";

import {
  Currency,
  IAction,
  ICost,
} from "~/components/pages/itemManagement/incidents/create/steps/new-costs/types";

import { Incident } from "~/components/pages/itemManagement/incidents/types";

import { Inspection } from "~/components/pages/itemManagement/inspections/types";

import { Observation } from "~/components/pages/itemManagement/observations/types";

import { EnvironmentalRecord } from "~/components/pages/recordManagement/environmental/types";

import {
  HealthEntry,
  Meeting,
} from "~/components/pages/recordManagement/meeting/types";

import type {
  GroupedBy,
  PaginatedDashboardTemplatesResponse,
  UserDashboard,
  Widget,
} from "@veiligh/reporting-core/domain";

import {
  QueryClause,
  RunQueryData,
  SaveQueryPayload,
  SavedQuery,
  SavedQueryById,
} from "~/components/pages/reports_old/query/types";

import { Metrics } from "~/components/pages/resourceManagement/employees/metric-response";

import {
  AssignedUsers,
  Classification,
  Inventory,
} from "~/components/pages/resourceManagement/inventory/type";

import { DefaultCurrency } from "~/components/pages/settings/currency-settings/type";

import {
  FormTemplate,
  Question,
} from "~/components/pages/settings/form-builder/types";

import { Tag } from "~/components/pages/settings/types";

import { SiteLocation } from "~/components/pages/sites/types";

import { AISearchResponse } from "~/components/shared/ai-search/types.ts";

import { CollectionLinkItem } from "~/components/ui/adusei-editor/type";

import { getConfig, loadConfig } from "~/lib/config/env";

import {
  DEFAULT_API_PAYLOAD,
  InventoryType,
  REQUEST_HEADER_APP_NAME,
} from "~/lib/constants";

import { Data, Role, User } from "~/lib/types";

import { Permission, waitForTimeout } from "~/lib/utils";

import {
  ApprovalDto,
  CollectionItemDto,
  DeleteApiV1ApprovalByIdApiArg,
  DeleteApiV1ApprovalByIdApiResponse,
  DeleteApiV1AttachmentsApiArg,
  DeleteApiV1AttachmentsApiResponse,
  DeleteApiV1AuditApiArg,
  DeleteApiV1AuditApiResponse,
  DeleteApiV1CostByIdApiArg,
  DeleteApiV1CostByIdApiResponse,
  DeleteApiV1CustomizationCurrencyByIdApiArg,
  DeleteApiV1CustomizationCurrencyByIdApiResponse,
  DeleteApiV1EmployeeAvatarByIdApiArg,
  DeleteApiV1EmployeeAvatarByIdApiResponse,
  DeleteApiV1EmployeeByIdApiArg,
  DeleteApiV1EnvironmentalRecordApiArg,
  DeleteApiV1EnvironmentalRecordApiResponse,
  DeleteApiV1EnvironmentalRecordByIdApiArg,
  DeleteApiV1EnvironmentalRecordByIdApiResponse,
  DeleteApiV1FindingApiArg,
  DeleteApiV1FindingApiResponse,
  DeleteApiV1FormByIdApiArg,
  DeleteApiV1FormByIdApiResponse,
  DeleteApiV1FormQuestionsByIdApiArg,
  DeleteApiV1FormQuestionsByIdApiResponse,
  DeleteApiV1GeneralInventoryApiArg,
  DeleteApiV1GeneralInventoryApiResponse,
  DeleteApiV1GeneralInventoryByIdApiArg,
  DeleteApiV1GeneralInventoryByIdApiResponse,
  DeleteApiV1HealthEntryApiArg,
  DeleteApiV1HealthEntryApiResponse,
  DeleteApiV1HealthEntryByIdApiArg,
  DeleteApiV1HealthEntryByIdApiResponse,
  DeleteApiV1IncidentApiArg,
  DeleteApiV1IncidentApiResponse,
  DeleteApiV1InspectionsApiArg,
  DeleteApiV1InspectionsApiResponse,
  DeleteApiV1ItemByDropDownTypeAndIdApiArg,
  DeleteApiV1ItemByDropDownTypeAndIdApiResponse,
  DeleteApiV1WorkHoursByIdApiArg,
  DeleteApiV1WorkHoursByIdApiResponse,
  DeleteApiV1MeetingEntryApiArg,
  DeleteApiV1MeetingEntryApiResponse,
  DeleteApiV1MeetingEntryByIdApiArg,
  DeleteApiV1ObservationApiArg,
  DeleteApiV1ObservationApiResponse,
  DeleteApiV1QueryApiArg,
  DeleteApiV1QueryApiResponse,
  DeleteApiV1RolesByIdApiArg,
  DeleteApiV1RolesByIdApiResponse,
  DeleteApiV1StoresByIdApiArg,
  DeleteApiV1StoresByIdApiResponse,
  DeleteApiV1StoresTransactionByIdApiArg,
  DeleteApiV1StoresTransactionByIdApiResponse,
  DeleteApiV1TagByIdApiArg,
  DeleteApiV1TagByModelTypeApiArg,
  DeleteApiV1TagByModelTypeApiResponse,
  DeleteApiV1UserDashboardBulkApiArg,
  DeleteApiV1UserDashboardBulkApiResponse,
  DeleteApiV1UserDashboardByUserDashboardIdApiArg,
  DeleteApiV1UserDashboardByUserDashboardIdApiResponse,
  DeleteApiV1UserDashboardWidgetByWidgetConfigurationIdApiArg,
  DeleteApiV1UserDashboardWidgetByWidgetConfigurationIdApiResponse,
  GetApiV1ActionsByModelIdApiArg,
  GetApiV1ActionsCommentByActionIdApiArg,
  GetApiV1ActionsSingleByIdApiArg,
  GetApiV1AlertsApiArg, // GetApiVbyVersionAlertsAndIdArg,
  GetApiV1ApprovalApiArg,
  GetApiV1AuditApiArg,
  GetApiV1AuditByIdApiArg,
  GetApiV1CollectionsGroupByApiArg,
  GetApiV1CollectionsScreensApiArg,
  GetApiV1CollectionsTypesApiArg, //
  GetApiV1CollectionsTypesApiResponse,
  GetApiV1CostByIdApiArg,
  GetApiV1CostByModelTypeAndModelIdApiArg,
  GetApiV1CustomizationCurrencyApiArg,
  GetApiV1CustomizationCurrencyDefaultApiArg,
  GetApiV1EmployeeApiArg,
  GetApiV1EmployeeAvatarByIdApiArg,
  GetApiV1EmployeeAvatarByIdApiResponse,
  GetApiV1EmployeeByIdApiArg,
  GetApiV1EmployeeMetricsByIdApiArg,
  GetApiV1EnvironmentalRecordApiArg,
  GetApiV1EnvironmentalRecordByIdApiArg,
  GetApiV1EnvironmentalRecordDataPointApiArg,
  GetApiV1EnvironmentalRecordDataPointApiResponse,
  GetApiV1FindingByIdApiArg,
  GetApiV1FormAllByFormTypeIdApiArg,
  GetApiV1FormByFormTypeIdApiArg,
  GetApiV1FormDetailsByIdApiArg,
  GetApiV1FormFormsByTypeApiArg,
  GetApiV1FormQuestionsApiArg,
  GetApiV1FormQuestionsByTypeApiArg,
  GetApiV1GeneralInventoryApiArg,
  GetApiV1GeneralInventoryByIdApiArg,
  GetApiV1GeneralInventoryByInventoryIdAssignedUsersApiArg,
  GetApiV1GeneralInventoryByInventoryIdDetailsApiArg,
  GetApiV1GeneralInventoryClassificationsApiArg,
  GetApiV1HealthEntryApiArg,
  GetApiV1IncidentApiArg,
  GetApiV1IncidentByIdApiArg,
  GetApiV1InspectionsApiArg,
  GetApiV1InspectionsByIdApiArg,
  GetApiV1WorkHoursApiArg,
  GetApiV1WorkHoursApiResponse,
  GetApiV1WorkHoursByIdApiArg,
  GetApiV1WorkHoursByIdApiResponse,
  GetApiV1WorkHoursMetricsApiArg,
  GetApiV1WorkHoursMetricsApiResponse,
  GetApiV1MeetingEntryApiArg,
  GetApiV1MeetingEntryByIdApiArg,
  GetApiV1ObservationApiArg,
  GetApiV1ObservationByIdApiArg,
  GetApiV1PermissionApiArg,
  GetApiV1PermissionApiResponse,
  GetApiV1PermissionMenuApiArg,
  GetApiV1PermissionRoleByRoleIdApiArg,
  GetApiV1PermissionUserApiArg,
  GetApiV1PermissionUserByUserIdApiArg,
  GetApiV1QueryApiArg,
  GetApiV1QueryGroupByByQueryIdApiArg,
  GetApiV1QuerySavedApiArg,
  GetApiV1QuerySavedByQueryIdApiArg,
  GetApiV1QuerySqlApiArg,
  GetApiV1RolesApiArg,
  GetApiV1RolesByIdApiArg,
  GetApiV1SitesApiArg,
  GetApiV1SitesByIdApiArg,
  GetApiV1SitesByModelTypeAndSiteIdApiArg,
  GetApiV1SitesByModelTypeAndSiteIdApiResponse,
  GetApiV1StoresApiArg,
  GetApiV1StoresTransactionByStoreIdApiArg,
  GetApiV1StoresTransactionByStoreIdApiResponse,
  GetApiV1StoresTransactionEmployeeByIdApiArg,
  GetApiV1StoresTransactionEmployeeByIdApiResponse,
  GetApiV1TagApiArg,
  GetApiV1UserDashboardApiArg,
  GetApiV1UserDashboardPaginatedApiArg,
  GetApiV1UserDashboardTogglePinByUserDashboardIdApiArg,
  GetApiV1UserDashboardTogglePinByUserDashboardIdApiResponse,
  GetApiV1UserToggleDisableByIdApiArg,
  GetApiV1WidgetApiArg,
  GetApiV1WidgetDaysSinceLastApiArg,
  MeetingEntryDto,
  MenuItem,
  PatchApiV1GeneralInventoryByInventoryIdMoveItemApiArg,
  PatchApiV1GeneralInventoryByInventoryIdMoveItemApiResponse,
  PatchApiV1GeneralInventoryByInventoryIdSitesAndSiteIdStockApiArg,
  PatchApiV1GeneralInventoryByInventoryIdSitesAndSiteIdStockApiResponse,
  PostApiV1ActionsApiArg,
  PostApiV1ActionsApiResponse,
  PostApiV1ActionsCommentApiArg,
  PostApiV1ActionsCommentApiResponse,
  PostApiV1AlertsApiArg,
  PostApiV1AlertsApiResponse,
  PostApiV1ApprovalApiArg,
  PostApiV1ApprovalApiResponse,
  PostApiV1AttachmentsApiArg,
  PostApiV1AttachmentsApiResponse,
  PostApiV1AttachmentsByModelTypeAndModelIdApiArg,
  PostApiV1AttachmentsByModelTypeAndModelIdApiResponse,
  PostApiV1AuditAssignApiArg,
  PostApiV1AuditCompleteApiArg,
  PostApiV1AuditCompleteApiResponse,
  PostApiV1AuthChangePasswordApiArg,
  PostApiV1AuthChangePasswordApiResponse,
  PostApiV1AuthLoginApiArg,
  PostApiV1AuthLoginApiResponse,
  PostApiV1AuthSetPasswordApiArg,
  PostApiV1CollectionsApiArg,
  PostApiV1CollectionsApiResponse,
  PostApiV1CollectionsLinkApiArg,
  PostApiV1CostApiArg,
  PostApiV1CostApiResponse,
  PostApiV1CustomizationCurrencyApiArg,
  PostApiV1CustomizationCurrencyApiResponse,
  PostApiV1DashboardGroupedAnalyticsByEmployeeIdApiArg,
  PostApiV1EmployeeApiArg,
  PostApiV1EmployeeAvatarByIdApiArg,
  PostApiV1EmployeeAvatarByIdApiResponse,
  PostApiV1EnvironmentalRecordApiArg,
  PostApiV1EnvironmentalRecordApiResponse,
  PostApiV1EnvironmentalRecordAttachmentByIdApiArg,
  PostApiV1EnvironmentalRecordAttachmentByIdApiResponse,
  PostApiV1FindingApiArg,
  PostApiV1FindingApiResponse,
  PostApiV1FormApiArg,
  PostApiV1FormApiResponse,
  PostApiV1FormQuestionsApiArg,
  PostApiV1FormQuestionsApiResponse,
  PostApiV1GeneralInventoryApiArg,
  PostApiV1GeneralInventoryApiResponse,
  PostApiV1GeneralInventoryAssignApiArg,
  PostApiV1GeneralInventoryAssignApiResponse,
  PostApiV1GeneralInventoryRestockApiArg,
  PostApiV1GeneralInventoryRestockApiResponse,
  PostApiV1HealthEntryApiArg,
  PostApiV1HealthEntryApiResponse,
  PostApiV1IncidentApiArg,
  PostApiV1IncidentApiResponse,
  PostApiV1InspectionsAssignApiArg,
  PostApiV1InspectionsAssignApiResponse,
  PostApiV1InspectionsCompleteApiArg,
  PostApiV1InspectionsCompleteApiResponse,
  PostApiV1ItemByDropDownTypeApiArg,
  PostApiV1ItemByDropDownTypeApiResponse,
  PostApiV1WorkHoursApiArg,
  PostApiV1WorkHoursApiResponse,
  PostApiV1MeetingEntryApiArg,
  PostApiV1MeetingEntryApiResponse,
  PostApiV1ObservationApiArg,
  PostApiV1ObservationApiResponse,
  PostApiV1QueryApiArg,
  PostApiV1QuerySavedApiArg,
  PostApiV1RolesApiArg,
  PostApiV1RolesApiResponse,
  PostApiV1StoresApiArg,
  PostApiV1StoresApiResponse,
  PostApiV1StoresTransactionApiArg,
  PostApiV1StoresTransactionApiResponse,
  PostApiV1TagApiArg,
  PostApiV1TagByModelTypeAndModelIdApiArg,
  PostApiV1TagByModelTypeAndModelIdApiResponse,
  PostApiV1TagByModelTypeApiArg,
  PostApiV1TagByModelTypeApiResponse,
  PostApiV1UserDashboardApiArg,
  PostApiV1UserDashboardApiResponse,
  PostApiV1UserDashboardConfigurationApiArg,
  PostApiV1UserDashboardConfigurationApiResponse,
  PutApiV1ActionsStatusByIdApiArg,
  PutApiV1ActionsStatusByIdApiResponse,
  PutApiV1AlertsByIdApiArg,
  PutApiV1AlertsByIdApiResponse,
  PutApiV1ApprovalByIdApiArg,
  PutApiV1ApprovalByIdApiResponse,
  PutApiV1CostByIdApiArg,
  PutApiV1CostByIdApiResponse,
  PutApiV1CustomizationCurrencyDefaultApiArg,
  PutApiV1CustomizationCurrencyDefaultApiResponse,
  PutApiV1DashboardStatusByModelTypeAndModelIdStatusTypeApiArg,
  PutApiV1DashboardStatusByModelTypeAndModelIdStatusTypeApiResponse,
  PutApiV1EmployeeByIdApiArg,
  PutApiV1EnvironmentalRecordByIdApiArg,
  PutApiV1EnvironmentalRecordByIdApiResponse,
  PutApiV1FindingByIdApiArg,
  PutApiV1FindingByIdApiResponse,
  PutApiV1FormByIdApiArg,
  PutApiV1FormByIdApiResponse,
  PutApiV1FormQuestionsByIdApiArg,
  PutApiV1FormQuestionsByIdApiResponse,
  PutApiV1GeneralInventoryByIdApiArg,
  PutApiV1GeneralInventoryByIdApiResponse,
  PutApiV1HealthEntryByIdApiArg,
  PutApiV1HealthEntryByIdApiResponse,
  PutApiV1IncidentByIdApiArg,
  PutApiV1IncidentByIdApiResponse,
  PutApiV1InspectionsByIdApiArg,
  PutApiV1InspectionsByIdApiResponse,
  PutApiV1ItemByDropDownTypeAndIdApiArg,
  PutApiV1ItemByDropDownTypeAndIdApiResponse,
  PutApiV1WorkHoursByIdApiArg,
  PutApiV1WorkHoursByIdApiResponse,
  PutApiV1MeetingEntryByIdApiArg,
  PutApiV1MeetingEntryByIdApiResponse,
  PutApiV1ObservationByIdApiArg,
  PutApiV1ObservationByIdApiResponse,
  PutApiV1PermissionRoleByIdApiArg,
  PutApiV1QuerySavedByQueryIdApiArg,
  PutApiV1RolesByIdApiArg,
  PutApiV1RolesByIdApiResponse,
  PutApiV1StoresByIdApiArg,
  PutApiV1StoresByIdApiResponse,
  PutApiV1StoresQuantityByIdApiArg,
  PutApiV1StoresQuantityByIdApiResponse,
  PutApiV1StoresTransactionByIdApiArg,
  PutApiV1StoresTransactionByIdApiResponse,
  PutApiV1TagByIdApiArg,
  PutApiV1UserDashboardByUserDashboardIdApiArg,
  PutApiV1UserDashboardByUserDashboardIdApiResponse,
  PutApiV1UserDashboardConfigurationByWidgetConfigurationIdApiArg,
  PutApiV1UserDashboardConfigurationByWidgetConfigurationIdApiResponse,
  PutApiV1UserDashboardConfigurationPositionApiArg,
  PutApiV1UserDashboardConfigurationPositionApiResponse,
  DeleteApiV1IncidentLostTimeByIdApiArg,
  DeleteApiV1IncidentLostTimeByIdApiResponse,
  PostApiV1IncidentLostTimeByIncidentIdApiArg,
  PostApiV1IncidentLostTimeByIncidentIdApiResponse,
  GetApiV1DashboardTemplatesApiArg,
  PostApiV1DashboardTemplatesByTemplateIdInstallApiResponse,
  PostApiV1DashboardTemplatesByTemplateIdInstallApiArg,
  PostApiV1UserDashboardByDashboardIdShareApiResponse,
  PostApiV1UserDashboardByDashboardIdShareApiArg,
  GetApiV1UserApiArg,
  PutApiV1ActionsByIdApiResponse,
  PutApiV1ActionsByIdApiArg,
  GetApiV1DashboardItemStatusByModelTypeAndModelIdApiArg,
  GetApiV1DashboardItemStatusByModelTypeAndModelIdApiResponse,
} from "./api/openapi.generated";

import {
  GetApiV1EligibilityPermitApiArg,
  GetApiV1EligibilityPermitByIdApiArg,
  PostApiV1EligibilityPermitApiArg,
  PutApiV1EligibilityPermitByIdApiArg,
  PutApiV1EligibilityPermitDisableByIdApiArg,
  PostApiV1EligibilityPermitDocumentApiArg,
  GetApiV1EligibilityPermitDocumentByEligibilityPermitIdApiArg,
  PutApiV1EligibilityPermitDocumentByIdApiArg,
  DeleteApiV1EligibilityPermitDocumentByIdApiArg,
  GetApiV1CompetencePermitApiArg,
  GetApiV1CompetencePermitByIdApiArg,
  PostApiV1CompetencePermitApiArg,
  PutApiV1CompetencePermitByIdApiArg,
  PutApiV1CompetencePermitDisableByIdApiArg,
  PostApiV1CompetencePermitDocumentApiArg,
  GetApiV1CompetencePermitDocumentByCompetencePermitIdApiArg,
  PutApiV1CompetencePermitDocumentByIdApiArg,
  DeleteApiV1CompetencePermitDocumentByIdApiArg,
  GetApiV1CompanyWidePermitApiArg,
  GetApiV1CompanyWidePermitByIdApiArg,
  PostApiV1CompanyWidePermitApiArg,
  PutApiV1CompanyWidePermitByIdApiArg,
  PostApiV1CompanyWidePermitDocumentApiArg,
  GetApiV1CompanyWidePermitDocumentByCompanyWidePermitIdApiArg,
  PutApiV1CompanyWidePermitDocumentByIdApiArg,
  DeleteApiV1CompanyWidePermitDocumentByIdApiArg,
  GetApiV1SiteSpecificPermitApiArg,
  GetApiV1SiteSpecificPermitByIdApiArg,
  PostApiV1SiteSpecificPermitApiArg,
  PutApiV1SiteSpecificPermitByIdApiArg,
  PostApiV1SiteSpecificPermitDocumentApiArg,
  GetApiV1SiteSpecificPermitDocumentBySiteSpecificPermitIdApiArg,
  PutApiV1SiteSpecificPermitDocumentByIdApiArg,
  DeleteApiV1SiteSpecificPermitDocumentByIdApiArg,
} from "./api/openapi.generated";

import { loadSessionStore } from "./session";

import { AuthState, authActions } from "./slice/auth";

import {
  GetResponseType,
  GetResponseTypeNotPaginated,
  GetSingleResponseType,
  MutationResponseType,
} from "./types";

const appHeader = (originUrl: URL) => {
  if (originUrl.hostname.includes(".")) {
    // Split the URL by the dot and get the first part

    const firstPart = originUrl.hostname.split(".")[0];

    return firstPart;
  }

  return null;
};

const version = "1";

export const baseQueryWithCamelize: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions = {}) => {
  await loadConfig();

  const config = getConfig(); // ✅ safe now: loadConfig() already awaited

  const apiUrl = config.VITE_APP_BASE_URL;

  const baseQuery = fetchBaseQuery({
    baseUrl: apiUrl,

    credentials: "same-origin",

    mode: "cors",

    prepareHeaders: async (headers, { getState }) => {
      const auth = await loadSessionStore();

      const originUrl = new URL(window.location.origin);

      const appName = appHeader(originUrl);

      const getStateAuth = getState();

      const persistedAuth = getStateAuth as typeof getStateAuth & {
        persistedReducer: { auth: AuthState };
      };

      const pAuth = persistedAuth.persistedReducer.auth;

      const token = pAuth?.accessToken || auth?.accessToken;

      const refreshToken = pAuth?.refreshToken || auth?.refreshToken;

      const triggerRefresh = pAuth?.triggerRefresh;

      if (appName) {
        headers.set(REQUEST_HEADER_APP_NAME, appName);
      }

      if (triggerRefresh) {
        if (refreshToken) {
          headers.set("Authorization", `Bearer ${refreshToken}`);
        }
      } else {
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }
      }

      return headers;
    },
  });

  let result = await baseQuery(args, api, extraOptions);

  if (result.data) {
    result.data = camelizeKeys(result.data as any);
  }

  if (result.error) {
    const redirectTo = window.location.pathname;

    const errorData = result.error.data
      ? camelizeKeys(result.error.data as any)
      : result.error;

    result.error = errorData as FetchBaseQueryError;

    const errorResponse = result.error as any;

    if (errorResponse && typeof errorResponse === "object") {
      errorResponse.status = result.meta?.response?.status as number;
    }

    if (result.error.status === 401) {
      api.dispatch(authActions.setTriggerRefresh(true));

      const refreshResult = await baseQuery(
        "/auth/applicant/refresh-token",

        api,

        extraOptions,
      );

      api.dispatch(authActions.setTriggerRefresh(false));

      if (refreshResult.data) {
        await waitForTimeout(1000);

        result = await baseQuery(args, api, extraOptions);
      } else {
        window.location.href = `/logout?redirectTo=${redirectTo}`;
      }
    }
  }

  return result;
};

export const restApi = createApi({
  baseQuery: baseQueryWithCamelize,
  tagTypes: ["RiskMatrix", "Notifications", "NotificationSettings"],
  endpoints: (builder) => ({
    logout: builder.query({
      query: (queryArg) => ({
        url: `/api/v${version}/auth/logout`,

        method: "POST",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    Login: builder.mutation<
      MutationResponseType<PostApiV1AuthLoginApiResponse>,
      PostApiV1AuthLoginApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/auth/login`,

        method: "POST",

        body: queryArg.loginRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    forgotPassword: builder.mutation({
      query: (queryArg) => ({
        url: `/api/v${version}/auth/forgot-password`,

        method: "POST",

        body: queryArg.forgotPasswordRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getInspections: builder.query<
      GetResponseType<Inspection>,
      GetApiV1InspectionsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/inspections`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          employeeId: queryArg.employeeId,

          "with-disabled": queryArg["with-disabled"],

          statuses: queryArg.statuses,

          typeIds: queryArg.typeIds,

          siteIds: queryArg.siteIds,
        },
      }),
    }),

    getAudits: builder.query<GetResponseType<Audit>, GetApiV1AuditApiArg>({
      query: (queryArg) => ({
        url: `/api/v${version}/audit`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          employeeId: queryArg.employeeId,

          "with-disabled": queryArg["with-disabled"],

          statuses: queryArg.statuses,

          typeIds: queryArg.typeIds,

          siteIds: queryArg.siteIds,
        },
      }),
    }),

    getAuditById: builder.query<
      GetSingleResponseType<Audit>,
      GetApiV1AuditByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/audit/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    assignAudit: builder.mutation<
      MutationResponseType<{ id: string }>,
      PostApiV1AuditAssignApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/audit/assign`,

        method: "POST",

        body: queryArg.auditAssignRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createAuditComplete: builder.mutation<
      MutationResponseType<PostApiV1AuditCompleteApiResponse>,
      PostApiV1AuditCompleteApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/audit/complete`,

        method: "POST",

        body: queryArg.completeAuditRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    addAuditTags: builder.mutation<
      PostApiV1TagByModelTypeAndModelIdApiResponse,
      PostApiV1TagByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/tag/${queryArg.modelType}/${queryArg.modelId}`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getEligibilityPermits: builder.query<any, GetApiV1EligibilityPermitApiArg>({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          employeeId: queryArg.employeeId,
          employeeIds: queryArg.employeeIds,
          expired: queryArg.expired,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getEligibilityPermitById: builder.query<
      GetSingleResponseType<EligibilityPermit>,
      GetApiV1EligibilityPermitByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit/${queryArg.id}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createEligibilityPermit: builder.mutation<
      MutationResponseType<EligibilityPermitProps>,
      PostApiV1EligibilityPermitApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit`,
        method: "POST",
        body: queryArg.createEligibilityPermitRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateEligibilityPermit: builder.mutation<
      any,
      PutApiV1EligibilityPermitByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit/${queryArg.id}`,
        method: "PUT",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        body: queryArg.createEligibilityPermitRequest,
      }),
    }),

    toggleEligibilityPermitStatusById: builder.mutation<
      any,
      PutApiV1EligibilityPermitDisableByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit/disable/${queryArg.id}`,
        method: "PUT",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getEligibilityPermitDocumentsByEligibilityPermit: builder.query<
      any,
      GetApiV1EligibilityPermitDocumentByEligibilityPermitIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit-document/${queryArg.eligibilityPermitId}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    createEligibilityPermitDocument: builder.mutation<
      any,
      PostApiV1EligibilityPermitDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit-document`,
        method: "POST",
        body: queryArg.body,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateEligibilityPermitDocumentById: builder.mutation<
      any,
      PutApiV1EligibilityPermitDocumentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit-document/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateEligibilityPermitDocumentRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteEligibilityPermitDocumentById: builder.mutation<
      any,
      DeleteApiV1EligibilityPermitDocumentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/eligibility-permit-document/${queryArg.id}`,
        method: "DELETE",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCompetencePermits: builder.query<any, GetApiV1CompetencePermitApiArg>({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          employeeId: queryArg.employeeId,
          employeeIds: queryArg.employeeIds,
          expired: queryArg.expired,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getCompetencePermitById: builder.query<
      GetSingleResponseType<CompetencePermit>,
      GetApiV1CompetencePermitByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit/${queryArg.id}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createCompetencePermit: builder.mutation<
      MutationResponseType<CompetencePermitProps>,
      PostApiV1CompetencePermitApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit`,
        method: "POST",
        body: queryArg.createCompetencePermitRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateCompetencePermit: builder.mutation<
      any,
      PutApiV1CompetencePermitByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit/${queryArg.id}`,
        method: "PUT",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        body: queryArg.createCompetencePermitRequest,
      }),
    }),

    toggleCompetencePermitStatusById: builder.mutation<
      any,
      PutApiV1CompetencePermitDisableByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit/disable/${queryArg.id}`,
        method: "PUT",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCompetencePermitDocumentsByCompetencePermit: builder.query<
      any,
      GetApiV1CompetencePermitDocumentByCompetencePermitIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit-document/${queryArg.competencePermitId}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    createCompetencePermitDocument: builder.mutation<
      any,
      PostApiV1CompetencePermitDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit-document`,
        method: "POST",
        body: queryArg.body,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateCompetencePermitDocumentById: builder.mutation<
      any,
      PutApiV1CompetencePermitDocumentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit-document/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateCompetencePermitDocumentRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteCompetencePermitDocumentById: builder.mutation<
      any,
      DeleteApiV1CompetencePermitDocumentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/competence-permit-document/${queryArg.id}`,
        method: "DELETE",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCompanyWidePermits: builder.query<
      GetResponseType<CompanyWidePermit>,
      GetApiV1CompanyWidePermitApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/company-wide-permit`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          expired: queryArg.expired,
          showVoided: queryArg.showVoided,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getCompanyWidePermitById: builder.query<
      GetSingleResponseType<CompanyWidePermit>,
      GetApiV1CompanyWidePermitByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/company-wide-permit/${queryArg.id}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createCompanyWidePermit: builder.mutation<
      MutationResponseType<CompanyWidePermitProps>,
      PostApiV1CompanyWidePermitApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/company-wide-permit`,
        method: "POST",
        body: queryArg.createCompanyWidePermitRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateCompanyWidePermitById: builder.mutation<
      any,
      PutApiV1CompanyWidePermitByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/company-wide-permit/${queryArg.id}`,
        method: "PUT",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        body: queryArg.createCompanyWidePermitRequest,
      }),
    }),

    getCompanyWidecompanyWidePermitDocumentDocuments: builder.query<
      any,
      GetApiV1CompanyWidePermitDocumentByCompanyWidePermitIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/company-wide-permit-document/${queryArg.companyWidePermitId}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getCompanyWidecompanyWidePermitDocumentDocumentsByCompanyWidePermit:
      builder.query<
        any,
        GetApiV1CompanyWidePermitDocumentByCompanyWidePermitIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/v${version}/company-wide-permit-document/${queryArg.companyWidePermitId}`,
          headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        }),
      }),

    createCompanyWidecompanyWidePermitDocumentDocument: builder.mutation<
      any,
      PostApiV1CompanyWidePermitDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/company-wide-permit-document`,
        method: "POST",
        body: queryArg.body,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateCompanyWidecompanyWidePermitDocumentDocumentById: builder.mutation<
      any,
      PutApiV1CompanyWidePermitDocumentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/company-wide-permit-document/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateCompanyWidePermitDocumentRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteCompanyWidecompanyWidePermitDocumentDocumentById: builder.mutation<
      any,
      DeleteApiV1CompanyWidePermitDocumentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/company-wide-permit-document/${queryArg.id}`,
        method: "DELETE",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getSiteSpecificPermits: builder.query<
      GetResponseType<SiteSpecificPermit>,
      GetApiV1SiteSpecificPermitApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/site-specific-permit`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          expired: queryArg.expired,
          showVoided: queryArg.showVoided,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getSiteSpecificPermitById: builder.query<
      GetSingleResponseType<SiteSpecificPermit>,
      GetApiV1SiteSpecificPermitByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/site-specific-permit/${queryArg.id}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createSiteSpecificPermit: builder.mutation<
      MutationResponseType<SiteSpecificPermitProps>,
      PostApiV1SiteSpecificPermitApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/site-specific-permit`,
        method: "POST",
        body: queryArg.createSiteSpecificPermitRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateSiteSpecificPermitById: builder.mutation<
      any,
      PutApiV1SiteSpecificPermitByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/site-specific-permit/${queryArg.id}`,
        method: "PUT",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        body: queryArg.createSiteSpecificPermitRequest,
      }),
    }),

    getSiteSpecificsiteSpecificPermitDocumentDocuments: builder.query<
      any,
      GetApiV1SiteSpecificPermitDocumentBySiteSpecificPermitIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/site-specific-permit-document/${queryArg.siteSpecificPermitId}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getSiteSpecificsiteSpecificPermitDocumentDocumentsBySiteSpecificPermit:
      builder.query<
        any,
        GetApiV1SiteSpecificPermitDocumentBySiteSpecificPermitIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/v${version}/site-specific-permit-document/${queryArg.siteSpecificPermitId}`,
          headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        }),
      }),

    createSiteSpecificsiteSpecificPermitDocumentDocument: builder.mutation<
      any,
      PostApiV1SiteSpecificPermitDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/site-specific-permit-document`,
        method: "POST",
        body: queryArg.body,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateSiteSpecificsiteSpecificPermitDocumentDocumentById: builder.mutation<
      any,
      PutApiV1SiteSpecificPermitDocumentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/site-specific-permit-document/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateSiteSpecificPermitDocumentRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteSiteSpecificsiteSpecificPermitDocumentDocumentById: builder.mutation<
      any,
      DeleteApiV1SiteSpecificPermitDocumentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/site-specific-permit-document/${queryArg.id}`,
        method: "DELETE",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getIncidentCosts: builder.query<
      GetResponseTypeNotPaginated<ICost>,
      GetApiV1CostByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/cost/${queryArg.modelType}/${queryArg.modelId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCostById: builder.query<
      GetSingleResponseType<ICost>,
      GetApiV1CostByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/cost/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createCosts: builder.mutation<
      MutationResponseType<PostApiV1CostApiResponse>,
      PostApiV1CostApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/cost`,

        method: "POST",

        body: queryArg.createCostRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateCostById: builder.mutation<
      MutationResponseType<PutApiV1CostByIdApiResponse>,
      PutApiV1CostByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/cost/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateCostRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteCostById: builder.mutation<
      MutationResponseType<DeleteApiV1CostByIdApiResponse>,
      DeleteApiV1CostByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/cost/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCostsByModel: builder.query<
      GetResponseTypeNotPaginated<ICost>,
      GetApiV1CostByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/cost/${queryArg.modelType}/${queryArg.modelId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getFindingsByModel: builder.query<
      GetResponseTypeNotPaginated<Finding>,
      GetApiV1CostByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/finding/${queryArg.modelType}/${queryArg.modelId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getIncident: builder.query<
      GetResponseType<Incident>,
      GetApiV1IncidentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/incident`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          employeeId: queryArg.employeeId,

          "with-disabled": queryArg["with-disabled"],

          statuses: queryArg.statuses,

          typeIds: queryArg.typeIds,

          siteIds: queryArg.siteIds,
        },
      }),
    }),

    getIncidentId: builder.query<
      GetSingleResponseType<Incident>,
      GetApiV1IncidentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/incident/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createIncidentLostTime: builder.mutation<
      PostApiV1IncidentLostTimeByIncidentIdApiResponse,
      PostApiV1IncidentLostTimeByIncidentIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/incident/lost-time/${queryArg.incidentId}`,

        method: "POST",

        body: queryArg.createLostTimeRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteIncidentLostTime: builder.mutation<
      DeleteApiV1IncidentLostTimeByIdApiResponse,
      DeleteApiV1IncidentLostTimeByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/incident/lost-time/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getApiV1WorkHours: builder.query<
      GetApiV1WorkHoursApiResponse,
      GetApiV1WorkHoursApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/work-hours`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    postApiV1WorkHours: builder.mutation<
      PostApiV1WorkHoursApiResponse,
      PostApiV1WorkHoursApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/work-hours`,
        method: "POST",
        body: queryArg.createWorkHoursRegisterRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getApiV1WorkHoursById: builder.query<
      GetApiV1WorkHoursByIdApiResponse,
      GetApiV1WorkHoursByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/work-hours/${queryArg.id}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    putApiV1WorkHoursById: builder.mutation<
      PutApiV1WorkHoursByIdApiResponse,
      PutApiV1WorkHoursByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/work-hours/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateWorkHoursRegisterRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteApiV1WorkHoursById: builder.mutation<
      DeleteApiV1WorkHoursByIdApiResponse,
      DeleteApiV1WorkHoursByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/work-hours/${queryArg.id}`,
        method: "DELETE",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getApiV1WorkHoursMetrics: builder.query<
      GetApiV1WorkHoursMetricsApiResponse,
      GetApiV1WorkHoursMetricsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/work-hours/metrics`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          siteId: queryArg.siteId,
          period: queryArg.period,
        },
      }),
    }),

    getObservation: builder.query<
      GetResponseType<Observation>,
      GetApiV1ObservationApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/observation`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          employeeId: queryArg.employeeId,

          "with-disabled": queryArg["with-disabled"],

          statuses: queryArg.statuses,

          typeIds: queryArg.typeIds,

          siteIds: queryArg.siteIds,
        },
      }),
    }),

    getObservationById: builder.query<
      GetSingleResponseType<Observation>,
      GetApiV1ObservationByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/observation/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createObservation: builder.mutation<
      MutationResponseType<PostApiV1ObservationApiResponse>,
      PostApiV1ObservationApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/observation`,

        method: "POST",

        body: queryArg.createObservationRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateObservationById: builder.mutation<
      MutationResponseType<PutApiV1ObservationByIdApiResponse>,
      PutApiV1ObservationByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/observation/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateObservationRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    addObservationTags: builder.mutation<
      PostApiV1TagByModelTypeAndModelIdApiResponse,
      PostApiV1TagByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/tag/Observation/${queryArg.modelId}`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    setPassword: builder.mutation({
      query: (queryArg: PostApiV1AuthSetPasswordApiArg) => ({
        url: `/api/v${version}/auth/set-password`,

        method: "POST",

        body: queryArg.setPasswordRequest,

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    getTags: builder.query<{ result: Tag[] }, GetApiV1TagApiArg>({
      query: (queryArg: GetApiV1TagApiArg) => ({
        url: `/api/v${version}/tag`,

        method: "GET",

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    createTag: builder.mutation({
      query: (queryArg: PostApiV1TagApiArg) => ({
        url: `/api/v${version}/tag`,

        method: "POST",

        body: queryArg.createTagRequest,

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    deleteTag: builder.mutation({
      query: (queryArg: DeleteApiV1TagByIdApiArg) => ({
        url: `/api/v${version}/tag/${queryArg.id}`,

        method: "DELETE",

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    updateTag: builder.mutation({
      query: (queryArg: PutApiV1TagByIdApiArg) => ({
        url: `/api/v${version}/tag/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateTagRequest,

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    getRoles: builder.query<
      GetResponseTypeNotPaginated<Role>,
      GetApiV1RolesApiArg
    >({
      query: (queryArg: GetApiV1RolesApiArg) => ({
        url: `/api/v${version}/roles`,

        method: "GET",

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    getRoleId: builder.query<
      GetSingleResponseType<Role>,
      GetApiV1RolesByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/roles/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateRole: builder.mutation<
      MutationResponseType<PutApiV1RolesByIdApiResponse>,
      PutApiV1RolesByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/roles/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateRoleRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteRole: builder.mutation<
      MutationResponseType<DeleteApiV1RolesByIdApiResponse>,
      DeleteApiV1RolesByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/roles/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getPermissions: builder.query<
      GetResponseTypeNotPaginated<GetApiV1PermissionApiResponse>,
      GetApiV1PermissionApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/permission`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getPermissionsByRoleId: builder.query<
      GetResponseTypeNotPaginated<Permission>,
      GetApiV1PermissionRoleByRoleIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/permission/role/${queryArg.roleId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getRoleModuleAccess: builder.query<
      { moduleKey: string; isEnabled: boolean }[],
      { roleId: string; "App-Name"?: string }
    >({
      query: (queryArg) => ({
        url: `/api/v1/permission/role/${queryArg.roleId}/module-access`,
        method: "GET",
      }),
      transformResponse: (response: any) =>
        response?.result !== undefined ? response.result : response,
    }),

    updateRoleModuleAccess: builder.mutation<
      void,
      {
        roleId: string;
        body: { moduleKey: string; isEnabled: boolean }[];
        "App-Name"?: string;
      }
    >({
      query: (queryArg) => ({
        url: `/api/v1/permission/role/${queryArg.roleId}/module-access`,
        method: "PUT",
        body: queryArg.body,
      }),
      transformResponse: (response: any) =>
        response?.result !== undefined ? response.result : response,
    }),

    getMyModuleAccess: builder.query<
      { moduleKey: string; isEnabled: boolean }[],
      void
    >({
      query: () => ({
        url: `/api/v1/permission/my-module-access`,
        method: "GET",
      }),
      transformResponse: (response: any) =>
        response?.result !== undefined ? response.result : response,
    }),

    getPermissionsByUserId: builder.query<
      GetResponseTypeNotPaginated<Permission>,
      GetApiV1PermissionUserByUserIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/permission/user/${queryArg.userId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getPermissionByUser: builder.query<
      GetResponseTypeNotPaginated<Permission>,
      GetApiV1PermissionUserApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/permission/user`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updatePermissionByRoleId: builder.mutation<
      MutationResponseType<PutApiV1PermissionRoleByIdApiArg>,
      PutApiV1PermissionRoleByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/permission/role/${queryArg.id}`,

        method: "PUT",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getSidebarPermissions: builder.query<
      GetResponseTypeNotPaginated<MenuItem>,
      GetApiV1PermissionMenuApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/permission/menu`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createRole: builder.mutation<
      PostApiV1RolesApiResponse,
      PostApiV1RolesApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/roles`,

        method: "POST",

        body: queryArg.createRoleRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createEmployee: builder.mutation({
      query: (queryArg: PostApiV1EmployeeApiArg) => ({
        url: `/api/v${version}/employee`,

        method: "POST",

        body: queryArg.createUserRequest,

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    deleteEmployee: builder.mutation({
      query: (queryArg: DeleteApiV1EmployeeByIdApiArg) => ({
        url: `/api/v${version}/employee/${queryArg.id}`,

        method: "DELETE",

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    getApprovals: builder.query<
      { result: { data: ApprovalDto[] } },
      GetApiV1ApprovalApiArg
    >({
      query: (queryArg: GetApiV1ApprovalApiArg) => ({
        url: `/api/v${version}/approval`,

        method: "GET",

        headers: {
          "App-Name": queryArg["App-Name"],
        },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    createApproval: builder.mutation<
      MutationResponseType<PostApiV1ApprovalApiResponse>,
      PostApiV1ApprovalApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/approval`,

        method: "POST",

        body: queryArg.createApprovalRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateApproval: builder.mutation<
      MutationResponseType<PutApiV1ApprovalByIdApiResponse>,
      PutApiV1ApprovalByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/approval/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateApprovalRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteApproval: builder.mutation<
      MutationResponseType<DeleteApiV1ApprovalByIdApiResponse>,
      DeleteApiV1ApprovalByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/approval/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getScreens: builder.query<
      { result: string[] },
      GetApiV1CollectionsScreensApiArg
    >({
      query: (queryArg: GetApiV1CollectionsScreensApiArg) => ({
        url: `/api/v${version}/collections/screens`,

        method: "GET",

        headers: {
          "App-Name": queryArg["App-Name"],
        },
      }),
    }),

    getAlerts: builder.query<{ result: Data }, GetApiV1AlertsApiArg>({
      query: (queryArg: GetApiV1AlertsApiArg) => ({
        url: `/api/v${version}/alerts`,

        method: "GET",

        headers: {
          "App-Name": queryArg["App-Name"],
        },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,
        },
      }),
    }),

    createAlerts: builder.mutation<
      MutationResponseType<PostApiV1AlertsApiResponse>,
      PostApiV1AlertsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/alerts`,

        method: "POST",

        body: queryArg.createAlertRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    UpdateAlertsAndId: builder.mutation<
      MutationResponseType<PutApiV1AlertsByIdApiResponse>,
      PutApiV1AlertsByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/alerts/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateAlertRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getToggleDisable: builder.mutation({
      query: (queryArg: GetApiV1UserToggleDisableByIdApiArg) => ({
        url: `/api/v${version}/alerts/toggle-disable/${queryArg.id}`,

        method: "GET",

        headers: {
          "App-name": queryArg["App-Name"],
        },
      }),
    }),

    getEmployeeById: builder.query<
      GetSingleResponseType<User>,
      GetApiV1EmployeeByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/employee/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateEmployee: builder.mutation<
      MutationResponseType<User>,
      PutApiV1EmployeeByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/employee/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateUserRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getMeetings: builder.query<
      GetResponseType<Meeting>,
      GetApiV1MeetingEntryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/meeting-entry`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getHealthEntries: builder.query<
      GetResponseType<HealthEntry>,
      GetApiV1HealthEntryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/health-entry`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    postHealthEntry: builder.mutation<
      PostApiV1HealthEntryApiResponse,
      PostApiV1HealthEntryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/health-entry`,

        method: "POST",

        body: queryArg.createHealthEntryRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateHealthEntryById: builder.mutation<
      PutApiV1HealthEntryByIdApiResponse,
      PutApiV1HealthEntryByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/health-entry/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateHealthEntryRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteHealthEntryById: builder.mutation<
      DeleteApiV1HealthEntryByIdApiResponse,
      DeleteApiV1HealthEntryByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/health-entry/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteMultipleHealthEntries: builder.mutation<
      DeleteApiV1HealthEntryApiResponse,
      DeleteApiV1HealthEntryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/health-entry`,

        method: "DELETE",

        body: queryArg.multipleIdsRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getMeetingById: builder.query<
      GetResponseType<MeetingEntryDto>,
      GetApiV1MeetingEntryByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/meeting-entry/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    // start desmond section of code

    getCurrency: builder.query<
      GetResponseTypeNotPaginated<Currency>,
      GetApiV1CustomizationCurrencyApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/customization/currency`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getDashboardActions: builder.query<
      GetResponseTypeNotPaginated<{
        status: string;

        count: number;

        data: { action: IAction; overdue: boolean }[];
      }>,
      void
    >({
      query: () => ({
        url: `/api/v${version}/dashboard/actions`,
      }),
    }),

    getActionsByModelId: builder.query<
      GetResponseType<IAction>,
      GetApiV1ActionsByModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/actions/${queryArg.modelId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          actionStatus: queryArg.actionStatus,

          startDate: queryArg.startDate,

          endDate: queryArg.endDate,

          page: queryArg.page,

          employeeId: queryArg.employeeId,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    changePassword: builder.mutation<
      MutationResponseType<PostApiV1AuthChangePasswordApiResponse>,
      PostApiV1AuthChangePasswordApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/auth/change-password`,

        method: "POST",

        body: queryArg.resetPasswordRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    CreateFindings: builder.mutation<
      MutationResponseType<PostApiV1FindingApiResponse>,
      PostApiV1FindingApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/finding`,

        method: "POST",

        body: queryArg.createFindingRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateFindings: builder.mutation<
      MutationResponseType<PutApiV1FindingByIdApiResponse>,
      PutApiV1FindingByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/finding/${queryArg.id}`,

        method: "PUT",

        body: queryArg.createFindingRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createIncident: builder.mutation<
      MutationResponseType<PostApiV1IncidentApiResponse>,
      PostApiV1IncidentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/incident`,

        method: "POST",

        body: queryArg.createIncidentRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateIncident: builder.mutation<
      MutationResponseType<PutApiV1IncidentByIdApiResponse>,
      PutApiV1IncidentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/incident/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateIncidentRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    bulkDeleteIncidents: builder.mutation<
      DeleteApiV1IncidentApiResponse,
      DeleteApiV1IncidentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/incident`,

        method: "DELETE",

        body: queryArg.multipleIdsRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    addIncidentTags: builder.mutation<
      PostApiV1TagByModelTypeAndModelIdApiResponse,
      PostApiV1TagByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/tag/${queryArg.modelType}/${queryArg.modelId}`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    associateTagAndModelType: builder.mutation<
      MutationResponseType<PostApiV1TagByModelTypeApiResponse>,
      PostApiV1TagByModelTypeApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/tag/${queryArg.modelType}`,

        method: "POST",

        body: queryArg.associateTagRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteAssociatedTagAndModelType: builder.mutation<
      MutationResponseType<DeleteApiV1TagByModelTypeApiResponse>,
      DeleteApiV1TagByModelTypeApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/tag/${queryArg.modelType}`,

        method: "DELETE",

        body: queryArg.associateTagRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    profile: builder.query<
      GetSingleResponseType<User>,
      GetApiV1EmployeeByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/employee/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getEmployees: builder.query<GetResponseType<User>, GetApiV1EmployeeApiArg>({
      query: (queryArg) => ({
        url: `/api/v${version}/employee`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getCollections: builder.query<
      MutationResponseType<PostApiV1CollectionsApiResponse>,
      PostApiV1CollectionsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/collections`,

        method: "POST",

        body: queryArg.getCollectionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getAISearch: builder.query<
      GetResponseTypeNotPaginated<AISearchResponse>,
      GetApiV1QuerySqlApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/query/sql`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: { queryPrompt: queryArg.queryPrompt },
      }),
    }),

    createFormOptions: builder.mutation<
      MutationResponseType<PostApiV1ItemByDropDownTypeApiResponse>,
      PostApiV1ItemByDropDownTypeApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/item/${queryArg.dropDownType}`,

        method: "POST",

        body: queryArg.createItemRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateFormOptions: builder.mutation<
      MutationResponseType<PutApiV1ItemByDropDownTypeAndIdApiResponse>,
      PutApiV1ItemByDropDownTypeAndIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/item/${queryArg.dropDownType}/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateItemRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteFormOptions: builder.mutation<
      MutationResponseType<DeleteApiV1ItemByDropDownTypeAndIdApiResponse>,
      DeleteApiV1ItemByDropDownTypeAndIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/item/${queryArg.dropDownType}/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createCustomCurrency: builder.mutation<
      MutationResponseType<PostApiV1CustomizationCurrencyApiResponse>,
      PostApiV1CustomizationCurrencyApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/customization/currency`,

        method: "POST",

        body: queryArg.createCurrencyRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteCustomCurrency: builder.mutation<
      MutationResponseType<DeleteApiV1CustomizationCurrencyByIdApiResponse>,
      DeleteApiV1CustomizationCurrencyByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/customization/currency/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCustomCurrency: builder.query<
      GetResponseTypeNotPaginated<DefaultCurrency>,
      GetApiV1CustomizationCurrencyApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/customization/currency`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateDefaultCurrent: builder.mutation<
      MutationResponseType<PutApiV1CustomizationCurrencyDefaultApiResponse>,
      PutApiV1CustomizationCurrencyDefaultApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/customization/currency/default`,

        method: "PUT",

        body: queryArg.updateDefaultCurrencyRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCustomCurrencyDefault: builder.query<
      GetSingleResponseType<DefaultCurrency>,
      GetApiV1CustomizationCurrencyDefaultApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/customization/currency/default`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getActions: builder.query<
      GetResponseType<IAction>,
      GetApiV1ActionsByModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/actions/${queryArg.modelId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          actionStatus: queryArg.actionStatus,

          startDate: queryArg.startDate,

          endDate: queryArg.endDate,

          page: queryArg.page,

          employeeId: queryArg.employeeId,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],

          includeVoided: queryArg.includeVoided,
        },
      }),
    }),

    updateActionsStatus: builder.mutation<
      PutApiV1ActionsStatusByIdApiResponse,
      PutApiV1ActionsStatusByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/actions/status/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateActionStatusRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateAction: builder.mutation<
      PutApiV1ActionsByIdApiResponse,
      PutApiV1ActionsByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/actions/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateActionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getGroupDashboardAnalytics: builder.mutation<
      MutationResponseType<DashboardProps>,
      PostApiV1DashboardGroupedAnalyticsByEmployeeIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/dashboard/grouped-analytics/${queryArg.employeeId}`,

        method: "POST",

        body: queryArg.getDashboardTabRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          startDate: queryArg.startDate,

          endDate: queryArg.endDate,

          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getUserDashboardPaginated: builder.query<
      GetResponseType<UserDashboard>,
      GetApiV1UserDashboardPaginatedApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/user-dashboard/paginated`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "only-pinned": queryArg["only-pinned"],
        },
      }),
    }),

    getUserDashboard: builder.query<
      GetResponseTypeNotPaginated<UserDashboard>,
      GetApiV1UserDashboardApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/user-dashboard`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: { "only-pinned": queryArg["only-pinned"] },
      }),
    }),

    getApiV1UserDashboardByUserDashboardId: builder.query<
      GetSingleResponseType<UserDashboard>,
      { userDashboardId: string; "App-Name"?: string }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/user-dashboard/${queryArg.userDashboardId}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"] },
      }),
    }),

    toggleDashboardPinning: builder.query<
      GetApiV1UserDashboardTogglePinByUserDashboardIdApiResponse,
      GetApiV1UserDashboardTogglePinByUserDashboardIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/user-dashboard/toggle-pin/${queryArg.userDashboardId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createUserDashboard: builder.mutation<
      MutationResponseType<PostApiV1UserDashboardApiResponse>,
      PostApiV1UserDashboardApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/user-dashboard`,

        method: "POST",

        body: queryArg.createUserDashboardRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateUserDashboard: builder.mutation<
      MutationResponseType<PutApiV1UserDashboardByUserDashboardIdApiResponse>,
      PutApiV1UserDashboardByUserDashboardIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/user-dashboard/${queryArg.userDashboardId}`,

        method: "PUT",

        body: queryArg.updateUserDashboardRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteUserDashboard: builder.mutation<
      MutationResponseType<DeleteApiV1UserDashboardByUserDashboardIdApiResponse>,
      DeleteApiV1UserDashboardByUserDashboardIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/user-dashboard/${queryArg.userDashboardId}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteUserDashboardBulk: builder.mutation<
      DeleteApiV1UserDashboardBulkApiResponse,
      DeleteApiV1UserDashboardBulkApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/user-dashboard/bulk`,

        method: "DELETE",

        body: queryArg.bulkDeleteDashboardRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createFormTemplates: builder.mutation<
      MutationResponseType<PostApiV1FormApiResponse>,
      PostApiV1FormApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form`,

        method: "POST",

        body: queryArg.createFormRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    // Queues the document for AI parsing. Returns immediately with a job id — the parse
    // (~1 minute for a large checklist) runs in the backend and survives navigating away.
    importFormDocument: builder.mutation<
      MutationResponseType<{ jobId: string }>,
      { body: FormData; "App-Name"?: string }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/import`,

        method: "POST",

        body: queryArg.body,

        headers: { "App-Name": queryArg["App-Name"] },
      }),
    }),

    // Status/result of a queued import. Polled while the builder is open; also used to apply a
    // finished import after the user has navigated away and come back via the notification.
    getFormImportStatus: builder.query<
      MutationResponseType<{
        jobId: string;
        status: "Queued" | "Processing" | "Done" | "Failed";
        fileName?: string;
        error?: string;
        // "template" not "result": the backend renames it to avoid colliding with AutoWrapper's
        // own "result" envelope key.
        template?: {
          name: string;
          warning?: string;
          sections: {
            title: string;
            description: string;
            questions: {
              label: string;
              description: string;
              type: string;
              required: boolean;
              options: { name: string }[];
              enableNotes: boolean;
              enableAttachment: boolean;
              unverified?: boolean;
            }[];
          }[];
        };
      }>,
      { jobId: string; "App-Name"?: string }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/import/${queryArg.jobId}`,

        headers: { "App-Name": queryArg["App-Name"] },
      }),
    }),

    updateFormTemplateById: builder.mutation<
      MutationResponseType<PutApiV1FormByIdApiResponse>,
      PutApiV1FormByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/form/${queryArg.id}`,

        method: "PUT",

        body: queryArg.createFormRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getFormQuestions: builder.query<
      GetResponseType<Question>,
      GetApiV1FormQuestionsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/questions`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          tags: queryArg.tags,
        },
      }),
    }),

    getFormForTemplates: builder.query<
      GetResponseType<FormTemplate>,
      GetApiV1FormByFormTypeIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/${queryArg.formTypeId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getFormForTemplatesById: builder.query<
      GetSingleResponseType<FormTemplate>,
      GetApiV1FormDetailsByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/form/details/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteTemplate: builder.mutation<
      DeleteApiV1FormByIdApiResponse,
      DeleteApiV1FormByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createFormQuestions: builder.mutation<
      MutationResponseType<PostApiV1FormQuestionsApiResponse>,
      PostApiV1FormQuestionsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/questions`,

        method: "POST",

        body: queryArg.createQuestionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteFormQuestions: builder.mutation<
      DeleteApiV1FormQuestionsByIdApiResponse,
      DeleteApiV1FormQuestionsByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/questions/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateFormQuestions: builder.mutation<
      PutApiV1FormQuestionsByIdApiResponse,
      PutApiV1FormQuestionsByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/questions/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateQuestionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getQueryManagement: builder.query<
      GetResponseTypeNotPaginated<SavedQuery>,
      GetApiV1QuerySavedApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/query/saved`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          searchQuery: queryArg.searchQuery,
          includeCloned: queryArg.includeCloned,
        },
      }),
    }),

    getSavedByQueryId: builder.query<
      MutationResponseType<SavedQueryById>,
      GetApiV1QuerySavedByQueryIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/query/saved/${queryArg.queryId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateSavedQueryById: builder.mutation<
      MutationResponseType<SaveQueryPayload>,
      PutApiV1QuerySavedByQueryIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/query/saved/${queryArg.queryId}`,

        method: "PUT",

        body: queryArg.createQueryRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    saveQueryManagement: builder.mutation<
      MutationResponseType<SaveQueryPayload>,
      PostApiV1QuerySavedApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/query/saved`,

        method: "POST",

        body: queryArg.createQueryRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    runQueryManagement: builder.mutation<
      GetResponseTypeNotPaginated<RunQueryData>,
      PostApiV1QueryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/query`,

        method: "POST",

        body: queryArg.queryNodeDto,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteQueryManagement: builder.mutation<
      MutationResponseType<DeleteApiV1QueryApiResponse>,
      DeleteApiV1QueryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/query`,

        method: "DELETE",

        body: queryArg.deleteQueryRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createAssignedInspection: builder.mutation<
      PostApiV1InspectionsAssignApiResponse,
      PostApiV1InspectionsAssignApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/inspections/assign`,

        method: "POST",

        body: queryArg.inspectionAssignRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateInspectionById: builder.mutation<
      PutApiV1InspectionsByIdApiResponse,
      PutApiV1InspectionsByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/inspections/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateInspectionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createInspectionComplete: builder.mutation<
      MutationResponseType<PostApiV1InspectionsCompleteApiResponse>,
      PostApiV1InspectionsCompleteApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/inspections/complete`,

        method: "POST",

        body: queryArg.completeInspectionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    assignInspection: builder.mutation<
      MutationResponseType<{ id: string }>,
      PostApiV1InspectionsAssignApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/inspections/assign`,

        method: "POST",

        body: queryArg.inspectionAssignRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getInspectionById: builder.query<
      GetSingleResponseType<Inspection>,
      GetApiV1InspectionsByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/inspections/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    addInspectionTags: builder.mutation<
      PostApiV1TagByModelTypeAndModelIdApiResponse,
      PostApiV1TagByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/tag/${queryArg.modelType}/${queryArg.modelId}`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getFormAllTypes: builder.query<
      GetResponseTypeNotPaginated<CollectionItemDto>,
      GetApiV1FormAllByFormTypeIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/all/${queryArg.formTypeId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getFindingById: builder.query<
      GetSingleResponseType<Finding>,
      GetApiV1FindingByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/finding/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteFindingById: builder.mutation<
      MutationResponseType<DeleteApiV1FindingApiResponse>,
      DeleteApiV1FindingApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/finding`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: { id: queryArg.id },
      }),
    }),

    getSingleActionId: builder.query<
      GetSingleResponseType<IAction>,
      GetApiV1ActionsSingleByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/actions/single/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createSingleAttachment: builder.mutation<
      MutationResponseType<PostApiV1AttachmentsApiResponse>,
      PostApiV1AttachmentsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/attachments`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createMultipleAttachments: builder.mutation<
      MutationResponseType<PostApiV1AttachmentsByModelTypeAndModelIdApiResponse>,
      PostApiV1AttachmentsByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/attachments/${queryArg.modelType}/${queryArg.modelId}`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteMultipleAttachments: builder.mutation<
      DeleteApiV1AttachmentsApiResponse,
      DeleteApiV1AttachmentsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/attachments`,

        method: "DELETE",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateDashboardPositionConfiguration: builder.mutation<
      MutationResponseType<PutApiV1UserDashboardConfigurationPositionApiResponse>,
      PutApiV1UserDashboardConfigurationPositionApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/user-dashboard/configuration/position`,

        method: "PUT",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getSuggestions: builder.mutation<
      GetResponseTypeNotPaginated<CollectionLinkItem>,
      PostApiV1CollectionsLinkApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/collections/link`,

        method: "POST",

        body: queryArg.getLinkCollectionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    saveComment: builder.mutation<
      MutationResponseType<PostApiV1ActionsCommentApiResponse>,
      PostApiV1ActionsCommentApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/actions/comment`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCommentByActionId: builder.query<
      GetResponseType<Comment>,
      GetApiV1ActionsCommentByActionIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/actions/comment/${queryArg.actionId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    createStores: builder.mutation<
      MutationResponseType<PostApiV1StoresApiResponse>,
      PostApiV1StoresApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getInventories: builder.query<
      GetResponseType<Inventory>,
      GetApiV1StoresApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    updateInventoryById: builder.mutation<
      MutationResponseType<PutApiV1StoresByIdApiResponse>,
      PutApiV1StoresByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateStockRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteInventoryById: builder.mutation<
      MutationResponseType<DeleteApiV1StoresByIdApiResponse>,
      DeleteApiV1StoresByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateInventoryQuantityById: builder.mutation<
      MutationResponseType<PutApiV1StoresQuantityByIdApiResponse>,
      PutApiV1StoresQuantityByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores/quantity/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateStockQuantityRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createInventoryTransaction: builder.mutation<
      MutationResponseType<PostApiV1StoresTransactionApiResponse>,
      PostApiV1StoresTransactionApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores/transaction`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getTransactionByStoreId: builder.query<
      GetResponseTypeNotPaginated<GetApiV1StoresTransactionByStoreIdApiResponse>,
      GetApiV1StoresTransactionByStoreIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores/transaction/${queryArg.storeId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getApiV1StoresTransactionEmployeeById: builder.query<
      GetResponseTypeNotPaginated<GetApiV1StoresTransactionEmployeeByIdApiResponse>,
      GetApiV1StoresTransactionEmployeeByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores/transaction/employee/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    updateStoresTransactionById: builder.mutation<
      MutationResponseType<PutApiV1StoresTransactionByIdApiResponse>,
      PutApiV1StoresTransactionByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores/transaction/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateStockTransactionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteStoresTransactionById: builder.mutation<
      DeleteApiV1StoresTransactionByIdApiResponse,
      DeleteApiV1StoresTransactionByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/stores/transaction/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getGeneralInventoryClassifications: builder.query<
      GetResponseTypeNotPaginated<Classification>,
      GetApiV1GeneralInventoryClassificationsApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/classifications`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createGeneralInventory: builder.mutation<
      MutationResponseType<PostApiV1GeneralInventoryApiResponse>,
      PostApiV1GeneralInventoryApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory`,

        method: "POST",

        body: queryArg.createInventoryRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getGeneralInventory: builder.query<
      GetResponseType<Inventory>,
      GetApiV1GeneralInventoryApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,
        },
      }),
    }),

    deleteGeneralInventory: builder.mutation<
      MutationResponseType<DeleteApiV1GeneralInventoryApiResponse>,
      DeleteApiV1GeneralInventoryApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory`,

        method: "DELETE",

        body: queryArg.multipleIdsRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getGeneralInventoryById: builder.query<
      GetSingleResponseType<Inventory>,
      GetApiV1GeneralInventoryByIdApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getInventoryDetailsById: builder.query<
      GetSingleResponseType<Inventory>,
      GetApiV1GeneralInventoryByInventoryIdDetailsApiArg & {
        type: InventoryType;
      }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/${queryArg.inventoryId}/details`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateGeneralInventoryById: builder.mutation<
      MutationResponseType<PutApiV1GeneralInventoryByIdApiResponse>,
      PutApiV1GeneralInventoryByIdApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateInventoryRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteGeneralInventoryById: builder.mutation<
      MutationResponseType<DeleteApiV1GeneralInventoryByIdApiResponse>,
      DeleteApiV1GeneralInventoryByIdApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateInventorySiteStock: builder.mutation<
      MutationResponseType<PatchApiV1GeneralInventoryByInventoryIdSitesAndSiteIdStockApiResponse>,
      PatchApiV1GeneralInventoryByInventoryIdSitesAndSiteIdStockApiArg & {
        type: InventoryType;
      }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/${queryArg.inventoryId}/sites/${queryArg.siteId}/stock`,

        method: "PATCH",

        body: queryArg.updateInventoryStockRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    moveStockBetweenSites: builder.mutation<
      MutationResponseType<PatchApiV1GeneralInventoryByInventoryIdMoveItemApiResponse>,
      PatchApiV1GeneralInventoryByInventoryIdMoveItemApiArg & {
        type: InventoryType;
      }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/${queryArg.inventoryId}/move-item`,

        method: "PATCH",

        body: queryArg.moveInventoryItemRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createGeneralRestock: builder.mutation<
      MutationResponseType<PostApiV1GeneralInventoryRestockApiResponse>,
      PostApiV1GeneralInventoryRestockApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/restock`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createGeneralInventoryAssign: builder.mutation<
      MutationResponseType<PostApiV1GeneralInventoryAssignApiResponse>,
      PostApiV1GeneralInventoryAssignApiArg & { type: InventoryType }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/assign`,

        method: "POST",

        body: queryArg.assignInventoryItemRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getInventoryAssignedUsers: builder.query<
      GetResponseTypeNotPaginated<AssignedUsers>,
      GetApiV1GeneralInventoryByInventoryIdAssignedUsersApiArg & {
        type: InventoryType;
      }
    >({
      query: (queryArg) => ({
        url: `/api/v1/${queryArg.type}-inventory/${queryArg.inventoryId}/assigned-users`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    // createGeneralInventoryAttachmentById: builder.mutation<

    //   MutationResponseType<PostApiV1GeneralInventoryAttachmentByIdApiResponse>,

    //   PostApiV1GeneralInventoryAttachmentByIdApiArg

    // >({

    //   query: (queryArg) => ({

    //     url: `/api/v1/${queryArg.type}-inventory/attachment/${queryArg.id}`,

    //     method: "POST",

    //     body: queryArg.body,

    //     headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

    //   }),

    // }),

    // end desmond section of code

    // start of Felix section of code

    createActions: builder.mutation<
      MutationResponseType<PostApiV1ActionsApiResponse>,
      PostApiV1ActionsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/actions`,

        method: "POST",

        body: queryArg.createActionRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createMeetingEntry: builder.mutation<
      MutationResponseType<PostApiV1MeetingEntryApiResponse>,
      PostApiV1MeetingEntryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/meeting-entry`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateMeetingEntrybyId: builder.mutation<
      MutationResponseType<PutApiV1MeetingEntryByIdApiResponse>,
      PutApiV1MeetingEntryByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/meeting-entry/${queryArg.id}`,

        method: "PUT",

        body: queryArg.updateMeetingEntryRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteMeetingbyId: builder.mutation<
      DeleteApiV1MeetingEntryApiResponse,
      DeleteApiV1MeetingEntryByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/meeting-entry/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteMultipleMeetings: builder.mutation<
      DeleteApiV1MeetingEntryApiResponse,
      DeleteApiV1MeetingEntryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/meeting-entry`,

        method: "DELETE",

        body: queryArg.multipleIdsRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getFormQuestionsByType: builder.query<
      GetResponseType<Question>,
      GetApiV1FormQuestionsByTypeApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/questions/by-type`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          questionTypes: queryArg.questionTypes,
        },
      }),
    }),

    getEnvironmentalRecords: builder.query<
      GetResponseType<EnvironmentalRecord>,
      GetApiV1EnvironmentalRecordApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/environmental-record`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,
        },
      }),
    }),

    deleteEnviromentalRecord: builder.mutation<
      DeleteApiV1EnvironmentalRecordByIdApiResponse,
      DeleteApiV1EnvironmentalRecordByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/environmental-record/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteMultipleEnviromentalRecord: builder.mutation<
      DeleteApiV1EnvironmentalRecordApiResponse,
      DeleteApiV1EnvironmentalRecordApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/environmental-record`,

        method: "DELETE",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getEnvironmentalRecordbyId: builder.query<
      GetSingleResponseType<EnvironmentalRecord>,
      GetApiV1EnvironmentalRecordByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/environmental-record/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getFormForTemplatesByFormType: builder.query<
      GetResponseType<FormTemplate>,
      GetApiV1FormFormsByTypeApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/form/forms/by-type`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          formTypes: queryArg.formTypes,

          page: queryArg.page,

          pageSize: queryArg.pageSize,
        },
      }),
    }),

    deleteEmployeeAvatarById: builder.mutation<
      DeleteApiV1EmployeeAvatarByIdApiResponse,
      DeleteApiV1EmployeeAvatarByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/employee/avatar/${queryArg.id}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    postEmployeeAvatarById: builder.mutation<
      MutationResponseType<PostApiV1EmployeeAvatarByIdApiResponse>,
      PostApiV1EmployeeAvatarByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/employee/avatar/${queryArg.id}`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getEmployeeAvatarById: builder.query<
      GetResponseType<GetApiV1EmployeeAvatarByIdApiResponse>,
      GetApiV1EmployeeAvatarByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/employee/avatar/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getEmployeeMetricsById: builder.query<
      GetSingleResponseType<Metrics>,
      GetApiV1EmployeeMetricsByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/employee/metrics/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteObservation: builder.mutation<
      DeleteApiV1ObservationApiResponse,
      DeleteApiV1ObservationApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/observation`,

        method: "DELETE",

        body: queryArg.multipleIdsRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateEnvironmentalRecordById: builder.mutation<
      MutationResponseType<PutApiV1EnvironmentalRecordByIdApiResponse>,
      PutApiV1EnvironmentalRecordByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/environmental-record/${queryArg.id}`,

        method: "PUT",

        body: queryArg.createEnvironmentalRecordRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteInspections: builder.mutation<
      DeleteApiV1InspectionsApiResponse,
      DeleteApiV1InspectionsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/inspections`,

        method: "DELETE",

        body: queryArg.multipleIdsRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteAudits: builder.mutation<
      DeleteApiV1AuditApiResponse,
      DeleteApiV1AuditApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/audit`,

        method: "DELETE",

        body: queryArg.multipleIdsRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    AddMeetingTags: builder.mutation<
      PostApiV1TagByModelTypeAndModelIdApiResponse,
      PostApiV1TagByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/tag/${queryArg.modelType}/${queryArg.modelId}`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getEnvironmentalRecordDataPoint: builder.query<
      GetSingleResponseType<GetApiV1EnvironmentalRecordDataPointApiResponse>,
      GetApiV1EnvironmentalRecordDataPointApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/environmental-record/data-point`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    // end of Felix section code

    // start eniku section of code

    createEnvironmentEntry: builder.mutation<
      MutationResponseType<PostApiV1EnvironmentalRecordApiResponse>,
      PostApiV1EnvironmentalRecordApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/environmental-record`,

        method: "POST",

        body: queryArg.createEnvironmentalRecordRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createEnvironmentalAttachment: builder.mutation<
      MutationResponseType<PostApiV1EnvironmentalRecordAttachmentByIdApiResponse>,
      PostApiV1EnvironmentalRecordAttachmentByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/environmental-record/attachment/${queryArg.id}`,

        method: "POST",

        body: queryArg.body,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCollectionsTypes: builder.query<
      GetResponseType<GetApiV1CollectionsTypesApiResponse>,
      GetApiV1CollectionsTypesApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/collections/types`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    //end of eniku section of code

    // start eugene section of code

    getGroupByQueryId: builder.query<
      GetResponseTypeNotPaginated<GroupedBy>,
      GetApiV1QueryGroupByByQueryIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/query/group-by/${queryArg.queryId}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getCollectionsGroupBy: builder.query<
      GetResponseTypeNotPaginated<GroupedBy>,
      GetApiV1CollectionsGroupByApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/collections/group-by`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    postWidgetConfiguration: builder.mutation<
      MutationResponseType<PostApiV1UserDashboardConfigurationApiResponse>,
      PostApiV1UserDashboardConfigurationApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/user-dashboard/configuration`,

        method: "POST",

        body: queryArg.createWidgetConfigurationRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateWidgetConfiguration: builder.mutation<
      MutationResponseType<PutApiV1UserDashboardConfigurationByWidgetConfigurationIdApiResponse>,
      PutApiV1UserDashboardConfigurationByWidgetConfigurationIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/user-dashboard/configuration/${queryArg.widgetConfigurationId}`,

        method: "PUT",

        body: queryArg.updateWidgetConfigurationRequest,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getWidgets: builder.query<
      GetResponseTypeNotPaginated<Widget>,
      GetApiV1WidgetApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/widget`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getDaysSinceLast: builder.query<
      GetSingleResponseType<Record<string, number>>,
      GetApiV1WidgetDaysSinceLastApiArg
    >({
      query: (queryArg) => {
        const queryString = queryArg?.injuryClassificationIds

          ?.map((id) => `injuryClassificationIds=${id}`)

          .join("&");

        return {
          url: `/api/v1/widget/days-since-last?${queryString}`,

          headers: {
            "App-Name": queryArg["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
          },
        };
      },
    }),

    deleteWidgetConfig: builder.mutation<
      DeleteApiV1UserDashboardWidgetByWidgetConfigurationIdApiResponse,
      DeleteApiV1UserDashboardWidgetByWidgetConfigurationIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/user-dashboard/widget/${queryArg.widgetConfigurationId}`,

        method: "DELETE",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getQuery: builder.query<
      GetResponseTypeNotPaginated<QueryClause>,
      GetApiV1QueryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/query`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: { parameterType: queryArg.parameterType },
      }),
    }),

    getV2Query: builder.query<{ result: string[] }, GetApiV1QueryApiArg>({
      query: (queryArg) => ({
        url: `/api/v${version}/query/item-type`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: { parameterType: queryArg.parameterType },
      }),
    }),

    // end eugene section of code

    // start of kwabena section of code

    closeResolvedItemsInDashboard: builder.mutation<
      MutationResponseType<PutApiV1DashboardStatusByModelTypeAndModelIdStatusTypeApiResponse>,
      PutApiV1DashboardStatusByModelTypeAndModelIdStatusTypeApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/dashboard/status/${queryArg.modelType}/${queryArg.modelId}/${queryArg.statusType}`,

        method: "PUT",

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: queryArg.reason ? { reason: queryArg.reason } : undefined,
      }),
    }),

    getItemStatus: builder.query<
      GetApiV1DashboardItemStatusByModelTypeAndModelIdApiResponse,
      GetApiV1DashboardItemStatusByModelTypeAndModelIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/dashboard/item/status/${queryArg.modelType}/${queryArg.modelId}`,
        headers: { "App-Name": queryArg["App-Name"] },
      }),
    }),

    getProjectSiteById: builder.query<
      GetSingleResponseType<SiteLocation>,
      GetApiV1SitesByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/sites/${queryArg.id}`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    getProjectSites: builder.query<
      GetResponseType<SiteLocation>,
      GetApiV1SitesApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/sites`,

        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },

        params: {
          siteIds: queryArg.siteIds,

          startDate: queryArg.startDate,

          endDate: queryArg.endDate,

          page: queryArg.page,

          pageSize: queryArg.pageSize,

          searchQuery: queryArg.searchQuery,

          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getProjectSitesByModelTypeAndSiteId: builder.query<
      GetApiV1SitesByModelTypeAndSiteIdApiResponse,
      GetApiV1SitesByModelTypeAndSiteIdApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/sites/${queryArg.modelType}/${queryArg.siteId}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          "with-disabled": queryArg["with-disabled"],
          siteIds: queryArg.siteIds,
          startDate: queryArg.startDate,
          endDate: queryArg.endDate,
        },
      }),
    }),

    // end of kwabena section of code
    getDashboardTemplates: builder.query<
      PaginatedDashboardTemplatesResponse,
      GetApiV1DashboardTemplatesApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/dashboard-templates`,
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
        params: {
          "published-only": queryArg["published-only"],
          searchQuery: queryArg.searchQuery,
          category: queryArg.category,
          page: queryArg.page,
          pageSize: queryArg.pageSize,
        },
      }),
    }),

    installDashboardTemplate: builder.mutation<
      PostApiV1DashboardTemplatesByTemplateIdInstallApiResponse,
      PostApiV1DashboardTemplatesByTemplateIdInstallApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/dashboard-templates/${queryArg.templateId}/install`,

        method: "POST",

        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
    }),

    uninstallDashboardTemplate: builder.mutation<
      void,
      { templateId: string; "App-Name"?: string }
    >({
      query: (queryArg) => ({
        url: `/api/v1/dashboard-templates/${queryArg.templateId}/uninstall`,
        method: "POST",
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
    }),
    getUsers: builder.query<
      GetResponseTypeNotPaginated<User>,
      GetApiV1UserApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/user`,
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
        params: queryArg || {},
      }),
    }),
    shareDashboard: builder.mutation<
      PostApiV1UserDashboardByDashboardIdShareApiResponse,
      PostApiV1UserDashboardByDashboardIdShareApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/user-dashboard/${queryArg.dashboardId}/share`,
        method: "POST",
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
        body: queryArg.shareDashboardRequest,
      }),
    }),
    cloneUserDashboard: builder.mutation<
      any,
      { dashboardId: string; title: string; "App-Name"?: string }
    >({
      query: (queryArg) => ({
        url: `/api/v1/user-dashboard/${queryArg.dashboardId}/clone`,
        method: "POST",
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
        body: { title: queryArg.title },
      }),
    }),
    getDashboardTemplateById: builder.query<
      any,
      { templateId: string; "App-Name"?: string }
    >({
      query: (queryArg) => ({
        url: `/api/v1/dashboard-templates/${queryArg.templateId}`,
        headers: {
          "App-Name": queryArg?.["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
        },
      }),
    }),

    // ── Feature Flags ──────────────────────────────────────────────────────

    getFeatureFlags: builder.query<
      { isAiUseEnabled: boolean },
      { "App-Name": string }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/feature-flags`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    // ── Permit Document Extraction (Phase 5) ───────────────────────────────

    extractPermitDocument: builder.mutation<
      {
        documentNumber: string | null;
        title: string | null;
        startDate: string | null;
        expiryDate: string | null;
      },
      { file: File; "App-Name": string }
    >({
      query: (queryArg) => {
        const body = new FormData();
        body.append("file", queryArg.file);
        return {
          url: `/api/v${version}/permit-document/extract`,
          method: "POST",
          body,
          headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        };
      },
    }),

    // ── Asset Management ──────────────────────────────────────────────────

    getAssets: builder.query<
      GetResponseType<
        import("~/components/pages/assetManagement/assets/types").Asset
      >,
      {
        "App-Name": string;
        page?: number;
        pageSize?: number;
        searchQuery?: string;
        "with-disabled"?: boolean;
      }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/asset`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          searchQuery: queryArg.searchQuery,
          "with-disabled": queryArg["with-disabled"],
        },
      }),
    }),

    getAssetById: builder.query<
      GetSingleResponseType<
        import("~/components/pages/assetManagement/assets/types").Asset
      >,
      { "App-Name": string; id: string }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/asset/${queryArg.id}`,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    createAsset: builder.mutation<
      MutationResponseType<{ id: string }>,
      { "App-Name": string; createAssetRequest: any }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/asset`,
        method: "POST",
        body: queryArg.createAssetRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    updateAsset: builder.mutation<
      MutationResponseType<void>,
      { "App-Name": string; id: string; updateAssetRequest: any }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/asset/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateAssetRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    deleteAsset: builder.mutation<
      MutationResponseType<void>,
      { "App-Name": string; id: string }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/asset/${queryArg.id}`,
        method: "DELETE",
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    bulkDeleteAssets: builder.mutation<
      MutationResponseType<void>,
      { "App-Name": string; multipleIdsRequest: { ids: string[] } }
    >({
      query: (queryArg) => ({
        url: `/api/v${version}/asset`,
        method: "DELETE",
        body: queryArg.multipleIdsRequest,
        headers: { [REQUEST_HEADER_APP_NAME]: queryArg[REQUEST_HEADER_APP_NAME] },
      }),
    }),

    // ── Work Hours Fill Task ──────────────────────────────────────────────────
    fillWorkHoursTask: builder.mutation<
      any,
      {
        id: string;
        hours: number;
        comment?: string;
        attachments?: File[];
        "App-Name"?: string;
      }
    >({
      query: (queryArg) => {
        const body = new FormData();
        body.append("Hours", queryArg.hours.toString());
        if (queryArg.comment) {
          body.append("Comment", queryArg.comment);
        }
        if (queryArg.attachments) {
          queryArg.attachments.forEach((file) => {
            body.append("Attachments", file);
          });
        }
        return {
          url: `/api/v${version}/actions/${queryArg.id}/fill-work-hours`,
          method: "POST",
          body,
          headers: {
            "App-Name": queryArg["App-Name"] || DEFAULT_API_PAYLOAD["App-Name"],
          },
        };
      },
    }),
  }),

  refetchOnMountOrArgChange: true,
});
